import { useEffect, useRef, type ReactNode } from 'react';

/**
 * Foglio che sale dal basso. Su schermi larghi diventa un riquadro centrato.
 * Chiude con Esc, con il tocco sullo sfondo e con il trascinamento verso il
 * basso della maniglia.
 */
export function Sheet({
  open,
  onClose,
  children,
  labelledBy,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  labelledBy?: string;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const dragStart = useRef<number | null>(null);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div
        className="animate-fade-in absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className="animate-sheet-in relative flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-[1.25rem] bg-surface sm:max-w-md sm:rounded-[1.25rem]"
        onTouchStart={(e) => {
          dragStart.current = e.touches[0]?.clientY ?? null;
        }}
        onTouchMove={(e) => {
          if (dragStart.current === null) return;
          const dy = (e.touches[0]?.clientY ?? 0) - dragStart.current;
          if (dy > 90) {
            dragStart.current = null;
            onClose();
          }
        }}
      >
        <div className="flex justify-center pt-2.5 pb-1">
          <span className="h-1 w-10 rounded-full bg-surface-2" aria-hidden="true" />
        </div>
        {children}
      </div>
    </div>
  );
}
